import { useEffect, useState } from "react";
import { Card, Col, Row, Typography, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ContestDashboard.css";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import { CONTEST_MANAGEMENT_BASE, API_BASE_URL } from "../../services/apiConstant";

const { Title, Text } = Typography;

interface Contest {
  id: string;
  title: string;
  status: number;
  createdAt: string;
  contestManagementImageResponses?: { fileUrl: string }[];
}

const ContestDashboard = () => {
  const navigate = useNavigate();

  const [counts, setCounts] = useState({
    drafts: 0,
    active: 0,
    upcoming: 0,
    past: 0,
  });

  const [lastSavedTitles, setLastSavedTitles] = useState({
    draft: "",
    active: "",
    upcoming: "",
    past: "",
  });

  const [loading, setLoading] = useState<boolean>(true); // ✅ Loading state

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // ✅ Start loader
      try {
        const token = getToken();
        if (!token) throw new Error("No token found");

        const res = await axios.get(API_BASE_URL + CONTEST_MANAGEMENT_BASE, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const decryptedData: Contest[] = await decryptMessage(res.data.Data, token);

        const grouped: Record<string, Contest[]> = {
          draft: [],
          active: [],
          upcoming: [],
          past: [],
        };

        decryptedData.forEach((c) => {
          switch (c.status) {
            case 0:
              grouped.draft.push(c);
              break;
            case 1:
              grouped.active.push(c);
              break;
            case 2:
              grouped.upcoming.push(c);
              break;
            case 3:
              grouped.past.push(c);
              break;
          }
        });

        setCounts({
          drafts: grouped.draft.length,
          active: grouped.active.length,
          upcoming: grouped.upcoming.length,
          past: grouped.past.length,
        });

        const latest = {
          draft: grouped.draft.length
            ? grouped.draft
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
              .createdAt
            : "",
          active: grouped.active.length
            ? grouped.active
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
              .createdAt
            : "",
          upcoming: grouped.upcoming.length
            ? grouped.upcoming
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
              .createdAt
            : "",
          past: grouped.past.length
            ? grouped.past
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
              .createdAt
            : "",
        };

        setLastSavedTitles(latest);
      } catch (err) {
        console.error("Failed to fetch contest data", err);
      } finally {
        setLoading(false); // ✅ Stop loader
      }
    };

    fetchData();
  }, []);

  const cards = [
    {
      key: "create",
      title: "Create New",
      desc: "Start a new contest",
      img: "../images/create.png",
      color: "#51E8FF",
      onClick: () => navigate("/contest-support/create"),
    },
    {
      key: "drafts",
      title: "Drafts",
      desc: `Total Drafts: ${counts.drafts}`,
      lastSaved: lastSavedTitles.draft,
      img: "../images/draft.png",
      color: "#E3E2E2",
      disabled: counts.drafts === 0,
      onClick: () => navigate("/contest-support/list", { state: { status: "0" } }),
    },
    {
      key: "active",
      title: "Active",
      desc: `Total Active: ${counts.active}`,
      lastSaved: lastSavedTitles.active,
      img: "../images/active.png",
      color: "#7CF578",
      disabled: counts.active === 0,
      onClick: () => navigate("/contest-support/list", { state: { status: "1" } }),
    },
    {
      key: "upcoming",
      title: "Upcoming",
      desc: `Total Upcoming: ${counts.upcoming}`,
      lastSaved: lastSavedTitles.upcoming,
      img: "../images/upcomming.png",
      color: "#F5A508",
      disabled: counts.upcoming === 0,
      onClick: () => navigate("/contest-support/list", { state: { status: "2" } }),
    },
    {
      key: "past",
      title: "Past Contest",
      desc: `Total Past: ${counts.past}`,
      lastSaved: lastSavedTitles.past,
      img: "../images/past.png",
      color: "#FA4B4B",
      disabled: counts.past === 0,
      onClick: () => navigate("/contest-support/list", { state: { status: "3" } }),
    },
  ];

  const handleCardClick = (card: any) => {
    if (!card.disabled && typeof card.onClick === "function") {
      card.onClick();
    }
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24, color: "#41787C" }}>
        Create and View Status For Contest Support!
      </Title>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <img
            src="/images/newLoading.gif" // <-- path to your GIF
            alt="Loading..."
            style={{ width: 200, height: 150 }}
          />
        </div>
      ) : (
        <Row gutter={[20, 20]}>
          {cards.map((card) => (
            <Col xs={24} sm={12} md={8} lg={6} key={card.key}>
              <Card
                hoverable={!card.disabled}
                onClick={() => handleCardClick(card)}
                cover={
                  <div
                    style={{
                      height: 180,
                      borderRadius: 8,
                      overflow: "hidden",
                      position: "relative",
                      opacity: card.disabled ? 0.5 : 1,
                      cursor: card.disabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <img
                      src={card.img}
                      alt={card.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: 12,
                        background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                        color: "#fff",
                      }}
                    >
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          color: card.color,
                          fontWeight: 600,
                        }}
                      >
                        {card.title}
                      </Title>
                      <Text style={{ color: "#ddd", fontSize: 13, display: "block" }}>
                        {card.desc}
                      </Text>

                      {card.lastSaved && (
                        <Text style={{ color: "#fff", fontSize: 12, opacity: 0.8, display: "block" }}>
                          Last Saved: {formatDate(card.lastSaved)}
                        </Text>
                      )}
                    </div>
                  </div>
                }
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                bodyStyle={{ padding: 0 }}
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default ContestDashboard;
